import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Plus, ShieldAlert } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, apiGet, adminMutate, useToast, usePermissions } from './primitives.js'
import { Field, TextInput } from './form.js'
import '../rh/rh.css'

/**
 * ACCÈS DE SERVICE — les applications qui APPELLENT Gestion.
 *
 * À ne pas confondre avec les intégrations voisines : celles-ci conservent les
 * secrets dont Gestion a besoin pour joindre l'extérieur. Ici, c'est
 * l'extérieur — la Main Courante, demain — qui vient lire.
 *
 * Le secret n'apparaît qu'UNE fois, à la création. Gestion n'en garde que
 * l'empreinte : il n'existe aucun moyen de le retrouver, et c'est voulu. La
 * seule réponse à « je l'ai perdu » est d'en créer un autre et de révoquer
 * celui-ci.
 */

interface Row {
  id: string; name: string; description: string | null
  scopes: string[]; secret_prefix: string
  created_at: string; created_by_name: string | null
  last_used_at: string | null; expires_at: string | null; revoked_at: string | null
}

/**
 * Ce que chaque périmètre ouvre, en clair. Le code reste affiché à côté : c'est
 * lui que le service appelant connaît. Un périmètre absent d'ici s'affiche
 * par son seul code, sans être caché.
 */
const PERIMETRES: Record<string, string> = {
  'presence.read': 'Présence du staff en soirée (Main Courante)',
  'sonde.read': 'Horaires des soirées, pour la supervision technique (Sonde)',
  'interphone.read': 'Soirée du jour, pour l’interphone IA (Sacha)',
  'billetterie.read': 'Personnes et droits de porte, pour la billetterie (LC Porte)',
}
const libellePerimetre = (s: string): string => (PERIMETRES[s] ? `${PERIMETRES[s]} — ${s}` : s)

const quand = (iso: string | null): string =>
  iso ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Paris' }).format(new Date(iso)) : 'jamais'

export function ServiceCredentialsPage() {
  const perms = usePermissions()
  const toast = useToast()
  const peut = (k: string): boolean => perms.permissions.includes(k)
  const [rows, setRows] = useState<Row[]>([])
  const [scopes, setScopes] = useState<string[]>([])
  const [creation, setCreation] = useState(false)
  const [nom, setNom] = useState('')
  const [description, setDescription] = useState('')
  const [choisis, setChoisis] = useState<string[]>(['presence.read'])
  const [secret, setSecret] = useState<string | null>(null)

  const charger = useCallback(async () => {
    const r = await apiGet<{ credentials: Row[]; scopes: string[] }>('/api/v1/admin/service-credentials')
    setRows(r.credentials); setScopes(r.scopes)
  }, [])
  useEffect(() => { void charger() }, [charger])

  const creer = async () => {
    const { data } = await adminMutate<{ error?: string; message?: string; secret?: string }>(
      'POST', '/api/v1/admin/service-credentials',
      { name: nom, description: description || null, scopes: choisis })
    if (data.error) { toast('error', data.message ?? 'Création refusée.'); return }
    setSecret(data.secret ?? null)
    setCreation(false); setNom(''); setDescription('')
    await charger()
  }

  const revoquer = async (r: Row) => {
    const { data } = await adminMutate<{ error?: string; message?: string }>(
      'POST', `/api/v1/admin/service-credentials/${r.id}/revoke`)
    if (data.error) { toast('error', data.message ?? 'Révocation impossible.'); return }
    toast('ok', `« ${r.name} » révoqué — plus aucune requête acceptée.`)
    await charger()
  }

  if (!peut('integration.read')) {
    return <div className="gd-panel adm-note">Vous n’avez pas le droit de consulter les accès de service.</div>
  }

  return (
    <div className="lc-page">
      <SmartBack />
      <Trail />
      <header className="hub-hero">
        <p className="hub-hero__kicker"><KeyRound size={13} aria-hidden="true" /> Intégrations</p>
        <h1>Accès de service</h1>
        <p className="hub-hero__sub">
          Les applications autorisées à appeler Gestion. À ne pas confondre avec les intégrations
          voisines, qui servent à ce que Gestion appelle l’extérieur.
        </p>
      </header>

      {peut('integration.write') ? (
        <div className="pr-barre">
          <span />
          <button type="button" className="lc-btn" onClick={() => setCreation(true)}>
            <Plus size={14} aria-hidden="true" /> Nouvel accès
          </button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="gd-panel adm-note">Aucun accès de service. Rien n’appelle Gestion aujourd’hui.</div>
      ) : (
        <ul className="pr-liste">
          {rows.map((r) => (
            <li key={r.id} className="pr-carte">
              <div className="pr-carte__id">
                <span className="pr-nom">{r.revoked_at ? <s>{r.name}</s> : r.name}</span>
                <span className="pr-meta">{r.description ?? '—'}</span>
              </div>
              <div className="pr-carte__etat">
                {r.revoked_at
                  ? <span className="pr-tag pr-tag--warn">Révoqué</span>
                  : <span className="pr-tag pr-tag--ok">Actif</span>}
                {r.scopes.map((s) => <span key={s} className="pr-tag" title={PERIMETRES[s] ?? s}>{s}</span>)}
              </div>
              <div className="pr-carte__horaires">
                <span><b>Secret</b> <code>{r.secret_prefix}…</code></span>
                <span><b>Dernier usage</b> {quand(r.last_used_at)}</span>
                {r.expires_at ? <span><b>Expire</b> {quand(r.expires_at)}</span> : null}
              </div>
              <div className="pr-carte__chiffres">
                {!r.revoked_at && peut('integration.write') ? (
                  <button type="button" className="lc-btn lc-btn--ghost" onClick={() => void revoquer(r)}>Révoquer</button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={creation} onClose={() => setCreation(false)} ariaLabel="Nouvel accès de service" width={620}>
        <h3>Nouvel accès de service</h3>
        <Field label="Nom" helper="Le nom du service appelant, tel que vous le reconnaîtrez dans six mois.">
          <TextInput value={nom} onChange={setNom} maxLength={80} placeholder="Main Courante" />
        </Field>
        <Field label="Description (optionnel)">
          <TextInput value={description} onChange={setDescription} maxLength={300} placeholder="Affichage du personnel présent en soirée" />
        </Field>
        <Field label="Périmètre" helper="Un accès ne reçoit jamais toute l’API : seulement ce dont il a besoin.">
          <div className="pr-filtres">
            {scopes.map((s) => (
              <label key={s} className="pr-tag" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={choisis.includes(s)}
                  onChange={(e) => setChoisis(e.target.checked ? [...choisis, s] : choisis.filter((x) => x !== s))} />
                {' '}{libellePerimetre(s)}
              </label>
            ))}
          </div>
        </Field>
        <div className="lc-modal__pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setCreation(false)}>Annuler</button>
          <button type="button" className="lc-btn" onClick={() => void creer()} disabled={nom.trim().length < 2 || choisis.length === 0}>
            Créer l’accès
          </button>
        </div>
      </Modal>

      {/* Le secret, montré UNE seule fois. Le refermer le perd définitivement. */}
      <Modal open={!!secret} onClose={() => setSecret(null)} ariaLabel="Secret de service" width={640}>
        <h3><ShieldAlert size={16} aria-hidden="true" /> Copiez ce secret maintenant</h3>
        <p className="lc-hint">
          Il n’est affiché qu’une seule fois. Gestion n’en conserve que l’empreinte : refermer cette
          fenêtre le perd définitivement, et la seule issue serait d’en créer un autre.
        </p>
        <pre className="tv-secret"><code>{secret}</code></pre>
        <div className="lc-modal__pied">
          <button type="button" className="lc-btn" onClick={() => setSecret(null)}>J’ai copié le secret</button>
        </div>
      </Modal>
    </div>
  )
}
